import { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { generateClient } from 'aws-amplify/api';
import { listESP32SensorData, listBloodPressureData } from './graphql/queries';
import { ESP32SensorData, ModelSortDirection, BloodPressureData } from './API'
import { sendEmail } from './ControlPanel';
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography';
import Skeleton from '@material-ui/lab/Skeleton';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';

const client = generateClient();

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        height: 122,
        marginBottom: theme.spacing(2),
        opacity: '95%'
    },
}));

export const getStatus = (value: number | null | undefined, type: string) => {
    let status = "";
    let color = "";

    // 检查 value 是否为数字，如果不是，则返回 Unknown
    if (typeof value !== 'number') {
        return { status: "Unknown", color: "#808080" }; // Grey for unknown
    }

    switch (type) {
        case 'diastolic': // Diastolic Blood Pressure
            if (value < 60) {
                status = "Low Diastolic Blood Pressure";
                color = "#FF4500"; // OrangeRed
            } else if (value >= 60 && value <= 80) {
                status = "Normal Diastolic Blood Pressure";
                color = "#32CD32"; // LimeGreen
            } else if (value > 80 && value <= 89) {
                status = "Elevated Diastolic Blood Pressure";
                color = "#FFD700"; // Gold
            } else {
                status = "High Diastolic Blood Pressure";
                color = "#FF0000"; // Red
            }
            break;

        case 'systolic': // Systolic Blood Pressure
            if (value < 90) {
                status = "Low Systolic Blood Pressure";
                color = "#FF4500"; // OrangeRed
            } else if (value >= 90 && value <= 120) {
                status = "Normal Systolic Blood Pressure";
                color = "#32CD32"; // LimeGreen
            } else if (value > 120 && value <= 139) {
                status = "Elevated Systolic Blood Pressure";
                color = "#FFD700"; // Gold
            } else {
                status = "High Systolic Blood Pressure";
                color = "#FF0000"; // Red
            }
            break;

        case 'heart_rate': // Heart Rate
            if (value < 60) {
                status = "Low Heart Rate (Bradycardia)";
                color = "#FF4500"; // OrangeRed
            } else if (value >= 60 && value <= 100) {
                status = "Normal Heart Rate";
                color = "#32CD32"; // LimeGreen
            } else if (value > 100 && value <= 120) {
                status = "Elevated Heart Rate (Mild Tachycardia)";
                color = "#FFD700"; // Gold
            } else {
                status = "High Heart Rate (Severe Tachycardia)";
                color = "#FF0000"; // Red
            }
            break;

        case 'pressure': // Atmospheric Pressure
            if (value < 980) {
                status = "Low Atmospheric Pressure";
                color = "#FF4500"; // OrangeRed
            } else if (value >= 980 && value <= 1050) {
                status = "Normal Atmospheric Pressure";
                color = "#32CD32"; // LimeGreen
            } else {
                status = "High Atmospheric Pressure";
                color = "#FF0000"; // Red
            }
            break;

        case 'temperature': // Temperature
            if (value < 16) {
                status = "Very Cold";
                color = "#0000FF"; // Blue: Represents extremely cold temperatures
            } else if (value >= 16 && value < 19) {
                status = "Cold";
                color = "#1E90FF"; // DodgerBlue: Signifies a cold environment
            } else if (value >= 19 && value <= 21) {
                status = "Cool";
                color = "#40E0D0"; // Turquoise: Slightly darker than Lightcyan for a cool temperature
            } else if (value > 21 && value <= 25) {
                status = "Comfortable";
                color = "#32CD32"; // LimeGreen: Comfortable and ideal humidity range
            } else if (value > 25 && value <= 28) {
                status = "Warm";
                color = "#FF8C00"; // DarkOrange: Represents a warm environment
            } else if (value > 28 && value <= 30) {
                status = "Hot";
                color = "#FF4500"; // OrangeRed: Signifies a hot environment
            } else {
                status = "Very Hot";
                color = "#FF0000"; // Red: Represents extremely hot temperatures
            }
            break;

        case 'humidity': // Humidity
            if (value < 30) {
                status = "Low Humidity";
                color = "#1E90FF"; // DodgerBlue: Represents dry, low humidity
            } else if (value >= 30 && value <= 60) {
                status = "Comfortable Humidity";
                color = "#32CD32"; // LimeGreen: Comfortable and ideal humidity range
            } else if (value > 60 && value <= 70) {
                status = "High Humidity";
                color = "#FF8C00"; // DarkOrange: Noticeably humid but still tolerable
            } else {
                status = "Very High Humidity";
                color = "#FF4500"; // OrangeRed: Uncomfortably high humidity
            }
            break;

        case 'spo2': // SpO2
            if (value < 85) {
                status = "Severe Hypoxemia";
                color = "#FF0000"; // Red: Critical condition, requires immediate medical intervention
            } else if (value >= 85 && value < 90) {
                status = "Moderate Hypoxemia";
                color = "#FF4500"; // OrangeRed: Low oxygen levels, requires close monitoring and treatment
            } else if (value >= 90 && value < 95) {
                status = "Mild Hypoxemia";
                color = "#FFD700"; // Gold: Slightly lower than normal, may require attention, especially for vulnerable patients
            } else if (value >= 95 && value <= 100) {
                status = "Normal Oxygen Saturation";
                color = "#32CD32"; // LimeGreen: Normal, healthy range, oxygen levels are sufficient
            }
            break;

        default:
            status = "Unknown Type";
            color = "#808080"; // Grey
            break;
    }

    return { status, color };
}

const VitalSign = ({ title, value, unit, status, statusColor, loading, time }:
    {
        title: string,
        value: string,
        unit: string,
        status: string,
        statusColor: string,
        time: string,
        loading: boolean
    }) => {
    const classes = useStyles(); // 调用 useStyles 获取样式
    return (
        <Paper className={classes.paper}>
            <Typography variant="subtitle1" gutterBottom>
                {title}
            </Typography>
            {/* Show Skeleton as a placeholder while loading, otherwise show the value */}
            {loading ? (
                <Skeleton variant="text" width={100} height={40} />
            ) : (
                <Typography variant="h4" component="div">
                    {value} {unit}
                </Typography>
            )}

            {/* Use Skeleton as a placeholder for status while loading */}
            {loading ? (
                <Skeleton variant="text" width={60} height={20} />
            ) : (
                <Typography variant="body2" style={{ color: statusColor }}>
                    {status}
                </Typography>
            )}

            {/* Show Skeleton as a placeholder while loading, otherwise show the value */}
            {loading ? (
                <Skeleton variant="text" width={100} height={40} />
            ) : (
                <Typography variant="subtitle2" component="div">
                    Update Time: {time}
                </Typography>
            )}
        </Paper>
    );
};

const HealthInfo = () => {
    const [sensorData, setSensorData] = useState<ESP32SensorData[]>([]);
    const [bloodPressureData, setBloodPressureData] = useState<BloodPressureData[]>([]);
    const [loading1, setLoading1] = useState(true);
    const [loading2, setLoading2] = useState(true);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");

    const systolic = bloodPressureData[0]?.systolic;
    const diastolic = bloodPressureData[0]?.diastolic;
    const heart_rate_2 = bloodPressureData[0]?.heart_rate;
    const pressure = sensorData[0]?.pressure;
    const temperature = sensorData[0]?.temperature;
    const humidity = sensorData[0]?.humidity;
    const spo2 = sensorData[0]?.spo2;
    const heart_rate = sensorData[0]?.heart_rate;
    const time_1 = new Date(Date.parse(sensorData[0]?.timestamp + 'Z')).toLocaleString('default', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }); // Convert to local time string
    const time_2 = new Date(Date.parse(bloodPressureData[0]?.date + 'Z')).toLocaleString('default', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }); // Convert to local time string

    useEffect(() => {
        fetchSensorData();
        fetchBloodPressureData();
    }, []);

    async function fetchSensorData() {
        try {
            setLoading1(true);
            const apiData = await client.graphql({
                query: listESP32SensorData,
                variables: {
                    device_id: 'esp32_001',
                    limit: 1,
                    sortDirection: ModelSortDirection.DESC
                }
            });
            // console.log(apiData)
            const items = apiData.data.listESP32SensorData?.items ?? [];
            setSensorData(items as ESP32SensorData[]);
        } catch (err) {
            console.error('Error fetching sensor data:', err);
        } finally {
            setLoading1(false);
        }
    }

    async function fetchBloodPressureData() {
        try {
            setLoading2(true);
            const apiData = await client.graphql({
                query: listBloodPressureData,
                variables: {
                    device_id: 'bp_device_001',
                    limit: 1,
                    sortDirection: ModelSortDirection.DESC
                }
            });
            // console.log(apiData)
            const items = apiData.data.listBloodPressureData?.items ?? [];
            setBloodPressureData(items as BloodPressureData[]);
        } catch (err) {
            console.error('Error fetching blood pressure data:', err);
        } finally {
            setLoading2(false);
        }
    }

    const handleEmailSend = useCallback(async (message: string) => {
        // Get checked array to check if 'doctor' or 'family' is enabled
        const checked = JSON.parse(localStorage.getItem('checked') || '[]');
        const doctorEmail = localStorage.getItem('doctorEmail');
        const familyEmail = localStorage.getItem('familyEmail');
        if ((checked.includes('doctor') && doctorEmail) || (checked.includes('family') && familyEmail)) {

            // Check and send email to the doctor if enabled and email is valid
            if (checked.includes('doctor') && doctorEmail) {
                console.log('Email sending to doctor:', doctorEmail);
                sendEmail(doctorEmail, message, "Doctor");
            }

            // Check and send email to the family if enabled and email is valid
            if (checked.includes('family') && familyEmail) {
                console.log('Email sending to family:', familyEmail);
                sendEmail(familyEmail, message, "Family Members");
            }
        }
    }, []);

    const checkForCriticalValues = useCallback(() => {
        let message = '';
        let sign = false;

        // Get prev value from localStorage
        const prevSystolic = parseFloat(localStorage.getItem('prevSystolic') || '0');
        const prevDiastolic = parseFloat(localStorage.getItem('prevDiastolic') || '0');
        const prevHeartRate = parseFloat(localStorage.getItem('prevHeartRate') || '0');
        const prevSpO2 = parseFloat(localStorage.getItem('prevSpO2') || '0');
        const prevTemperature = parseFloat(localStorage.getItem('prevTemperature') || '0');
        const prevHumidity = parseFloat(localStorage.getItem('prevHumidity') || '0');

        if (systolic !== null && systolic !== undefined && diastolic !== null && diastolic !== undefined) {
            if ((systolic >= 140 || diastolic >= 90) && (systolic !== prevSystolic || diastolic !== prevDiastolic)) {
                message += "Warning: Blood pressure is at critically high levels!\n";
                sign = true;
            }
        }

        if (heart_rate !== null && heart_rate !== undefined && heart_rate > 120 && heart_rate !== prevHeartRate) {
            message += "Warning: Heart rate is critically high!\n";
            sign = true;
        }

        if (spo2 !== null && spo2 !== undefined && spo2 < 90 && spo2 !== prevSpO2) {
            message += "Warning: SpO2 is critically low!\n";
            sign = true;
        }

        if (temperature !== null && temperature !== undefined && (temperature > 26 || temperature < 19) && temperature !== prevTemperature) {
            message += "Warning: Temperature is at dangerous levels!\n";
            sign = true;
        }

        if (humidity !== null && humidity !== undefined && (humidity < 30 || humidity > 60) && humidity !== prevHumidity) {
            message += "Warning: Humidity is outside the comfortable range!\n";
            sign = true;
        }

        if (sign) {
            setAlertMessage(message);
            setAlertOpen(true);
            handleEmailSend(message);
            // Save current Value
            localStorage.setItem('prevSystolic', systolic?.toString() || '0');
            localStorage.setItem('prevDiastolic', diastolic?.toString() || '0');
            localStorage.setItem('prevHeartRate', heart_rate?.toString() || '0');
            localStorage.setItem('prevSpO2', spo2?.toString() || '0');
            localStorage.setItem('prevTemperature', temperature?.toString() || '0');
            localStorage.setItem('prevHumidity', humidity?.toString() || '0');
        }
    }, [systolic, diastolic, heart_rate, spo2, temperature, humidity, handleEmailSend]);

    useEffect(() => {
        if (!loading1 && !loading2) {
            checkForCriticalValues();
        }
    }, [sensorData, bloodPressureData, loading1, loading2, checkForCriticalValues]);

    const handleAlertClose = () => {
        setAlertOpen(false);
    };

    const vitalSigns = [
        { title: "Temperature", value: temperature, unit: "°C", type: "temperature", loading: loading1, time: time_1 },
        { title: "Humidity", value: humidity, unit: "%", type: "humidity", loading: loading1, time: time_1 },
        { title: "Air Pressure", value: pressure, unit: "hPa", type: "pressure", loading: loading1, time: time_1 },
        { title: "SpO2", value: spo2, unit: "%", type: "spo2", loading: loading1, time: time_1 },
        { title: "Heart Rate (Real Time)", value: heart_rate, unit: "bpm", type: "heart_rate", loading: loading1, time: time_1 },
        { title: "Systolic Pressure", value: systolic, unit: "mmHg", type: "systolic", loading: loading2, time: time_2 },
        { title: "Diastolic Pressure", value: diastolic, unit: "mmHg", type: "diastolic", loading: loading2, time: time_2 },
        { title: "Heart Rate (Sphygmometer)", value: heart_rate_2, unit: "bpm", type: "heart_rate", loading: loading2, time: time_2 },
    ];

    return (
        <div>
            <h1>Health Info</h1>
            {vitalSigns.map(({ title, value, unit, type, loading, time }) => {
                const { status, color } = getStatus(value, type);
                return (
                    <VitalSign
                        key={title}
                        title={title}
                        value={value?.toString() ?? 'null'}
                        unit={unit}
                        status={status}
                        statusColor={color}
                        time={time}
                        loading={loading}
                    />
                );
            })}
            <Snackbar
                open={alertOpen}
                onClose={handleAlertClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                style={{ marginTop: '80px' }}
            >
                <Alert onClose={handleAlertClose} severity="error">
                    {alertMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default HealthInfo;
